import {Transaction} from "../../src/providers/transactions.store"
import {PAYMENT_STATUS, PAYMENT_TYPE_ENUM, TRANSACTION_TYPE_ENUM} from "../types/enums";
import dstvImage from "../assets/images/dstv.png";
import walletImage from "../assets/images/wallet.png";
import gotvImage from "../assets/images/gotv.png";
import startimesImage from "../assets/images/starTimes.png";
import mtnImage from "../assets/images/mtn.png";
import airtelImage from "../assets/images/airtel.png";
import gloImage from "../assets/images/glo.png";
import nineMobileImage from "../assets/images/9mobile.png";
import React from "react";
import {Spinner} from "reactstrap";

export class TransactionHelper {
    static getTransactionTypeDisplay(type?: TRANSACTION_TYPE_ENUM) {
        const typeMap: Record<TRANSACTION_TYPE_ENUM, string> = {
            [TRANSACTION_TYPE_ENUM.AIRTIME]: "Airtime Purchase",
            [TRANSACTION_TYPE_ENUM.DATA_TOP_UP]: "Data Purchase",
            [TRANSACTION_TYPE_ENUM.CABLE_SUBSCRIPTION]: "Cable TV Subscription",
            [TRANSACTION_TYPE_ENUM.ELECTRICITY]: "Electricity Bill",
            [TRANSACTION_TYPE_ENUM.WALLET_TOPUP]: "Wallet Top-up",
            [TRANSACTION_TYPE_ENUM.TRANSFER]: "Money Transfer",
            [TRANSACTION_TYPE_ENUM.VOUCHER_PURCHASE]: "Voucher Purchase",
            [TRANSACTION_TYPE_ENUM.VOUCHER_REDEMPTION]: "Voucher Redemption",
        };

        return type ? typeMap[type] : "Transaction";
    };

// Helper function to get status title
    static getStatusTitle(payment_status: PAYMENT_STATUS) {
        const statusMap: Record<PAYMENT_STATUS, string> = {
            [PAYMENT_STATUS.SUCCESS]: "Transaction Successful",
            [PAYMENT_STATUS.INITIATED]: "Transaction Pending",
            [PAYMENT_STATUS.PENDING]: "Transaction Pending",
            [PAYMENT_STATUS.FAILED]: "Transaction Failed",
            [PAYMENT_STATUS.REFUNDED]: "Transaction Refunded",
            [PAYMENT_STATUS.UNVERIFIED]: "Transaction Not Verified",
        };

        return statusMap[payment_status] || "Transaction Status";
    }

// Helper function to get recipient information based on transaction type
    static getRecipientInfo(transaction: Transaction) {
        const {
            transaction_type,
            direction,
            senders_account_name,
            phone_number,
            cable_customer_number,
            meter_number
        } = transaction;

        const recipientMap: Record<TRANSACTION_TYPE_ENUM, { label: string; value: string }> = {
            [TRANSACTION_TYPE_ENUM.AIRTIME]: {label: "To", value: phone_number || ""},
            [TRANSACTION_TYPE_ENUM.DATA_TOP_UP]: {label: "To", value: phone_number || ""},
            [TRANSACTION_TYPE_ENUM.CABLE_SUBSCRIPTION]: {label: "Smart Card", value: cable_customer_number || ""},
            [TRANSACTION_TYPE_ENUM.ELECTRICITY]: {label: "Meter Number", value: meter_number || ""},
            [TRANSACTION_TYPE_ENUM.WALLET_TOPUP]: {label: "From", value: senders_account_name || "Wallet Top-up"},
            [TRANSACTION_TYPE_ENUM.TRANSFER]: {
                label: direction === "credit" ? "From" : "To",
                value: senders_account_name || phone_number || "Transfer"
            },
            [TRANSACTION_TYPE_ENUM.VOUCHER_PURCHASE]: {
                label: direction === "credit" ? "From" : "To",
                value: senders_account_name || phone_number || "Voucher Purchase"
            },
            [TRANSACTION_TYPE_ENUM.VOUCHER_REDEMPTION]: {
                label: direction === "credit" ? "From" : "To",
                value: senders_account_name || phone_number || "Voucher Redemption"
            },
        };

        return recipientMap[transaction_type] || {
            label: "Recipient",
            value: phone_number || senders_account_name || "N/A"
        };
    };

// Format date helper
//     static formatTransactionDate(dateString: string) {
//         return new Date(dateString).toLocaleString("en-US", {
//             month: "short",
//             day: "2-digit",
//             year: "numeric",
//             hour: "2-digit",
//             minute: "2-digit"
//         });
//     };

// Format payment type helper
    static formatPaymentType(paymentType: string) {
        return paymentType.replace('_', ' ').toLowerCase();
    }
    static LoadingState() {
        return (
            <div className="fixed top-1/2 left-1/2 text-center mx-auto py-4">
                <Spinner size={"sm"} color={"#616060"} />
            </div>
        )
    }

    static EmptyState() {
        return (
            <div className="text-center py-8">
                <div className="text-gray-400 text-4xl mb-2">📝</div>
                <p className="text-gray-500 font-normal">No transactions found</p>
                <p className="text-gray-400 text-sm mt-1">Your transactions will appear here</p>
            </div>
        )
    }

// Helper component for error state
    static ErrorState({error, loadAgain}: { error: string, loadAgain?: ()=>{} }) {
        return (
            <div className="text-center py-4">
                <div className="text-red-400 text-2xl mb-2">⚠️</div>
                <p className="text-red-600">Failed to load transactions</p>
                <p className="text-gray-500 text-sm mt-1">{error}</p>
                <button
                    className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    onClick={async () => {await loadAgain?.()}}
                >
                    Try Again
                </button>
            </div>
        )
    }

// Helper function to get transaction logo
    static getTransactionLogo(transaction: Partial<Transaction>) {
        const {transaction_type, network_provider, payment_type} = transaction;

        // Cable TV providers
        if (transaction_type === TRANSACTION_TYPE_ENUM.CABLE_SUBSCRIPTION ||
            transaction_type === TRANSACTION_TYPE_ENUM.ELECTRICITY) {
            const provider = transaction.cable_provider?.toLowerCase();
            if (provider?.includes('dstv')) return dstvImage;
            if (provider?.includes('gotv')) return gotvImage;
            if (provider?.includes('startimes')) return startimesImage;
            return dstvImage; // default cable
        }

        // Telecom providers
        if (network_provider) {
            switch (network_provider.toLowerCase()) {
                case 'mtn':
                    return mtnImage;
                case 'airtel':
                    return airtelImage;
                case 'glo':
                    return gloImage;
                case '9mobile':
                    return nineMobileImage;
                default:
                    return mtnImage;
            }
        }

        // Wallet transactions
        if (transaction_type === TRANSACTION_TYPE_ENUM.WALLET_TOPUP ||
            payment_type === PAYMENT_TYPE_ENUM.VIRTUAL) {
            return walletImage;
        }

        // Data and airtime fallback
        if (transaction_type === TRANSACTION_TYPE_ENUM.DATA_TOP_UP ||
            transaction_type === TRANSACTION_TYPE_ENUM.AIRTIME) {
            return mtnImage;
        }

        return walletImage;
    };
    static getTransactionDescription(transaction: Transaction) {
        const {direction, status, payment_type, senders_account_name, phone_number} = transaction;

        if (![PAYMENT_STATUS.SUCCESS, PAYMENT_STATUS.FAILED, PAYMENT_STATUS.REFUNDED].includes(status)) {
            return "Processing...";
        }

        const directionText = direction === "credit" ? "From" : "To";

        if ([PAYMENT_TYPE_ENUM.CARD, PAYMENT_TYPE_ENUM.VIRTUAL].includes(payment_type as PAYMENT_TYPE_ENUM)) {
            return `${directionText} ${senders_account_name || "Unknown Account"}`;
        }

        return `${directionText} ${phone_number || "Unknown"}`;
    };


    static formatTransactionDate(dateString: string) {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

        if (diffInHours < 24) {
            return date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        }

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };
}